/* eslint-disable no-unused-vars */
import { useState, useEffect } from 'react';
import {
  Typography,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Divider,
  Box,
  alpha,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Link,
  Button,
  Tooltip,
  IconButton,
} from '@mui/material';
import ElectricBoltIcon from '@mui/icons-material/ElectricBolt';
import FormButtonControls from '@/components/forms/formButtonControls/FormButtonControls';
import api from '@/services/ApiService';
import ToastMessage from '@/components/toasts/ToastMessage';
import { toast } from 'react-toastify';
import RefreshIcon from '@mui/icons-material/Refresh';

const USVConfig = ({
  next,
  back,
  isLastStep,
  isFirstStep,
  handleCancel,
  deployment,
}) => {
  const [data, setData] = useState([]);
  const [loading, setIsLoading] = useState(true);
  const [selectedUsvs, setSelectedUsvs] = useState(
    deployment?.config?.upsArr || [],
  );

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const response = await api.get('/asset?type=ups');
      const data = response.data.result;
      setData(data);
    } catch (error) {
      toast.error(
        <ToastMessage
          title="Fehler"
          message="Die USVs konnten nicht geladen werden"
        />,
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleChange = (event) => {
    const value = event.target.value;
    // Limit to maximum 2 USVs
    setSelectedUsvs(value);
  };

  const handleSave = () => {
    next({ upsArr: selectedUsvs });
  };
  return (
    <Card
      elevation={2}
      sx={{
        borderRadius: 2,
        overflow: 'visible',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
      }}
    >
      <CardHeader
        avatar={<ElectricBoltIcon color="primary" />}
        title={<Typography variant="h6">USV Konfiguration</Typography>}
        sx={{
          bgcolor: (theme) => alpha(theme.palette.primary.light, 0.1),
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      />

      <CardContent sx={{ pt: 3, pb: 2, flex: 1 }}>
        {data.length === 0 ? (
          <Box sx={{ textAlign: 'center' }}>
            <Alert severity="info" sx={{ mb: 3 }}>
              Es wurden noch keine USVs konfiguriert
            </Alert>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button
                component={Link}
                href="/assets"
                target="_blank"
                variant="outlined"
              >
                USVs verwalten
              </Button>
              <Tooltip title="USV Liste aktualisieren">
                <IconButton size="small" onClick={fetchData} disabled={loading}>
                  <RefreshIcon
                    sx={{
                      animation: loading ? 'spin 1s linear infinite' : 'none',
                      '@keyframes spin': {
                        '0%': { transform: 'rotate(0deg)' },
                        '100%': { transform: 'rotate(360deg)' },
                      },
                    }}
                  />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        ) : (
          <>
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}
            >
              <Alert severity="info" sx={{ flex: 1, mr: 2 }}>
                Wählen Sie alle USVs aus, die überwacht werden sollen
              </Alert>
              <Button
                component={Link}
                href="/assets"
                target="_blank"
                variant="outlined"
                size="small"
              >
                USVs verwalten
              </Button>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'start' }}>
              <FormControl fullWidth>
                <InputLabel>USVs auswählen</InputLabel>
                <Select
                  multiple
                  value={selectedUsvs}
                  onChange={handleChange}
                  label="USVs auswählen"
                >
                  {data.map((usv) => (
                    <MenuItem key={usv._id} value={usv._id}>
                      {usv.title || usv.identifier}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box
                sx={{ height: '56px', display: 'flex', alignItems: 'center' }}
              >
                <Tooltip title="USV Liste aktualisieren">
                  <IconButton
                    size="small"
                    onClick={fetchData}
                    disabled={loading}
                  >
                    <RefreshIcon
                      sx={{
                        animation: loading ? 'spin 1s linear infinite' : 'none',
                        '@keyframes spin': {
                          '0%': { transform: 'rotate(0deg)' },
                          '100%': { transform: 'rotate(360deg)' },
                        },
                      }}
                    />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </>
        )}
      </CardContent>

      <Divider />

      <CardActions sx={{ justifyContent: 'flex-end' }}>
        <FormButtonControls
          next={next && handleSave}
          back={back}
          cancel={handleCancel}
          isFirstStep={isFirstStep}
          isLastStep={isLastStep}
          disabled={selectedUsvs.length === 0}
        />
      </CardActions>
    </Card>
  );
};

export default USVConfig;
